import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fmtEUR } from "@/lib/api";
import { holdTotal } from "@/lib/deliveryHolds";
import { PauseCircle, PlayCircle, Trash2, X, User as UserIcon, MapPin } from "lucide-react";

/**
 * DeliveryHoldsDialog — list of held delivery drafts. Click a card to
 * resume; trash icon to drop. Mirrors the Retail HoldsDialog visual
 * vocabulary so operators recognise the pattern immediately.
 */
export default function DeliveryHoldsDialog({ open, onClose, holds, onResume, onDelete }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        hideClose
        className="max-w-2xl border-2 border-slate-300 shadow-2xl p-0 gap-0 overflow-hidden"
        data-testid="delivery-holds-dialog"
      >
        <DialogHeader className="px-6 py-4 bg-amber-50 border-b-2 border-amber-300">
          <DialogTitle className="flex items-center gap-3 text-2xl font-heading font-bold text-amber-800">
            <PauseCircle size={28} />
            Παραγγελίες σε Αναμονή ({holds.length})
          </DialogTitle>
          <div className="text-sm text-amber-700 mt-1">
            Πάτα μια παραγγελία για επαναφορά στον editor
          </div>
        </DialogHeader>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto">
          {holds.length === 0 && (
            <div className="col-span-full text-center py-12">
              <PauseCircle size={48} className="mx-auto text-slate-300 mb-2" />
              <div className="text-slate-400 text-base font-medium">
                Δεν υπάρχουν παραγγελίες σε αναμονή.
              </div>
            </div>
          )}
          {holds.map((h) => {
            const total = holdTotal(h);
            const items = h.items || [];
            const customerName = h.customer?.name || "—";
            const addressLabel = h.address
              ? [h.address.street, h.address.street_no].filter(Boolean).join(" ") + (h.address.area ? ", " + h.address.area : "")
              : "—";
            return (
              <div
                key={h.id}
                className="border-2 border-slate-300 rounded-xl bg-white shadow-sm hover:shadow-md hover:border-emerald-500 active:scale-[0.98] cursor-pointer transition-all overflow-hidden"
                data-testid={`delivery-hold-${h.id}`}
                onClick={() => onResume(h)}
              >
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex justify-between items-center text-xs">
                  <div className="font-mono text-slate-600 font-bold">
                    {new Date(h.created_at).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="text-slate-500 font-bold">
                    {items.length} είδ{items.length === 1 ? "ος" : "η"}
                  </div>
                </div>
                <div className="px-3 py-2 space-y-1 text-xs">
                  <div className="flex items-center gap-1 text-slate-700 truncate">
                    <UserIcon size={11} className="text-slate-400 shrink-0" />
                    <span className="font-bold truncate">{customerName}</span>
                  </div>
                  <div className="flex items-start gap-1 text-slate-600 truncate">
                    <MapPin size={11} className="text-slate-400 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{addressLabel}</span>
                  </div>
                </div>
                <div className="px-3 py-2 flex items-baseline justify-between">
                  <span className="font-heading font-bold text-slate-700">Σύνολο</span>
                  <span className="font-mono tabular text-2xl font-extrabold text-emerald-700">
                    {fmtEUR(total)}
                  </span>
                </div>
                <div className="px-3 pb-2 text-[10px] text-slate-500 line-clamp-2 min-h-[28px]">
                  {items.map((it) => `${it.qty}× ${it.description}`).join(" • ")}
                </div>
                <div className="grid grid-cols-3 gap-0 border-t-2 border-slate-200">
                  <button
                    onClick={(e) => { e.stopPropagation(); onResume(h); }}
                    className="col-span-2 h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base flex items-center justify-center gap-2"
                    data-testid={`delivery-hold-resume-${h.id}`}
                  >
                    <PlayCircle size={18} />
                    Επαναφορά
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(h); }}
                    className="h-12 bg-red-50 hover:bg-red-100 text-red-700 font-bold border-l-2 border-slate-200 flex items-center justify-center"
                    data-testid={`delivery-hold-del-${h.id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-4 py-3 border-t-2 border-slate-200 bg-slate-50 flex justify-end">
          <Button
            onClick={onClose}
            variant="outline"
            className="h-11 px-6 border-2 border-slate-400 text-slate-800 hover:bg-white font-bold"
            data-testid="delivery-holds-back"
          >
            <X size={16} className="mr-1.5" />
            Επιστροφή
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
